<template>
  <Teleport to="body">
    <el-drawer
      v-model="visible"
      :size="drawerWidth"
      direction="rtl"
      :append-to-body="true"
      :destroy-on-close="false"
      class="evidence-diagnosis-drawer-custom"
      :with-header="true"
    >
      <!-- 头部：严谨工程级标题与状态 (与离线分析弹框保持完全一致) -->
      <template #header>
        <div class="drawer-header-content">
          <div class="header-left">
            <div class="drawer-title-row">
              <v-icon size="20" color="#2655f1" class="mr-2">mdi-robot-outline</v-icon>
              <h3 class="drawer-main-title">单告警码证据链深度诊断</h3>
            </div>
            <div class="drawer-sub-meta">
              <span class="meta-item">{{ targetStationName }}</span>
              <span class="meta-divider">/</span>
              <span class="meta-item font-weight-bold">{{ targetEquipmentName }}</span>
              <span class="meta-divider">/</span>
              <span class="meta-item code-tag">{{ targetAlarmCode }}</span>
              <span v-if="alarmStartTimeText" class="meta-divider">/</span>
              <span v-if="alarmStartTimeText" class="meta-item">{{ alarmStartTimeText }}</span>
            </div>
          </div>
          <div class="header-actions">
            <v-btn
              variant="outlined"
              color="#2655f1"
              size="small"
              rounded="pill"
              class="btn-re-diagnose"
              prepend-icon="mdi-refresh"
              :loading="isDiagnosing"
              @click="handleRestartDiagnosis"
            >
              重新诊断
            </v-btn>
          </div>
        </div>
      </template>

      <!-- 抽屉主滚动区域 -->
      <div class="drawer-body pa-6 overflow-y-auto">
        <EvidenceDiagnosisView
          ref="diagnosisViewRef"
          v-if="visible"
          :alarm-record="alarmRecord"
          :standalone="false"
          @loading-change="val => isDiagnosing = val"
        />
      </div>
    </el-drawer>
  </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import EvidenceDiagnosisView from './evidence-diagnosis/EvidenceDiagnosisView.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  alarmRecord: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue'])

const diagnosisViewRef = ref(null)
const isDiagnosing = ref(false)

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const drawerWidth = computed(() => {
  if (typeof window !== 'undefined' && window.innerWidth < 1400) {
    return '95vw'
  }
  return '1160px'
})

const targetStationName = computed(() => props.alarmRecord?.stationName || props.alarmRecord?.station_name || '储能电站')
const targetEquipmentName = computed(() => {
  const raw = props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || props.alarmRecord?.uuid || ''
  return raw && raw !== '--' ? raw : '储能受检设备'
})
const targetAlarmCode = computed(() => props.alarmRecord?.alarmCode || props.alarmRecord?.code || 'A1_02_00_1012')
const alarmStartTimeText = computed(() => {
  const t = props.alarmRecord?.alarmTime || props.alarmRecord?.startTime || props.alarmRecord?.lastTime || props.alarmRecord?.start_time || ''
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : ''
})

function handleRestartDiagnosis() {
  if (diagnosisViewRef.value?.startDiagnosis) {
    diagnosisViewRef.value.startDiagnosis()
  }
}
</script>

<!-- 全局覆盖 Element Plus Teleport 抽屉样式，对齐离线分析的紧凑高度与无缝过渡 -->
<style lang="scss">
.evidence-diagnosis-drawer-custom {
  .el-drawer__header {
    margin-bottom: 0 !important;
    padding: 16px 24px !important;
    border-bottom: 1px solid #e2e8f0 !important;
    display: flex !important;
    align-items: center !important;
  }

  .el-drawer__close-btn {
    margin-left: 24px !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 8px !important;
    color: #64748b !important;
    font-size: 18px !important;
    transition: all 0.2s ease !important;

    &:hover {
      background: #f1f5f9 !important;
      color: #0f172a !important;
    }
  }

  .el-drawer__body {
    padding: 0 !important;
    background: #f8fafc !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
}
</style>

<style scoped lang="scss">
.drawer-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  padding-right: 8px;

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .drawer-title-row {
      display: flex;
      align-items: center;

      .drawer-main-title {
        font-size: 16px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
      }
    }

    .drawer-sub-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #64748b;

      .meta-item {
        display: inline-flex;
        align-items: center;

        &.code-tag {
          background: #eff6ff;
          color: #2655f1;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 600;
        }
      }

      .meta-divider {
        color: #cbd5e1;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;

    .btn-re-diagnose {
      height: 32px !important;
      border-radius: 16px !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
      padding: 0 14px !important;
      border-color: #b8c5ff !important;
      background: #ffffff !important;
      box-shadow: 0 1px 3px rgba(38, 85, 241, 0.08);
      transition: all 0.2s ease;

      &:hover {
        background: #f0f4ff !important;
        border-color: #2655f1 !important;
        box-shadow: 0 2px 8px rgba(38, 85, 241, 0.16);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}

.drawer-body {
  flex: 1;
  background: #f8fafc;
  min-height: 0;
}
</style>
